
import React, { useEffect, useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "animate.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./FlashDeals.css";
import secureLocalStorage from "./storage";
import DealImage from "./DealImage";

const CustomPrevArrow = ({ onClick }) => (
  <button className="fd-prev-arrow" onClick={onClick}>
    <i className="las la-angle-left" />
  </button>
);

const CustomNextArrow = ({ onClick }) => (
  <button className="fd-next-arrow" onClick={onClick}>
    <i className="las la-angle-right" />
  </button>
);

function FlashDeals() {
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);

  const fetchBanners = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API}website/deals?type=flash`
      );

      if (data?.success && data.data.length > 0) {
        setBanners(data.data);
      } else {
        setBanners([]); // khaali rakho — yeh error nahi, normal state hai
      }
    } catch (err) {
      setError("Failed to load deals");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  // Countdown timer — nearest expiry_time se calculate hota hai
  useEffect(() => {
    if (banners.length === 0) return;

    const validExpiries = banners
      .map((d) => d.expiry_time)
      .filter(Boolean)
      .map((t) => new Date(t).getTime());

    if (validExpiries.length === 0) {
      setTimeLeft(null);
      return;
    }

    const nearestExpiry = Math.min(...validExpiries);

    const tick = () => {
      const diff = nearestExpiry - Date.now();
      if (diff <= 0) {
        setTimeLeft("ENDED");
        // Remove expired deal(s) locally. No extra API call, so no request loop
        // even if the browser clock and the server clock differ slightly.
        setBanners((prev) =>
          prev.filter((d) => !d.expiry_time || new Date(d.expiry_time).getTime() > Date.now())
        );
        return;
      }
      const h = String(Math.floor(diff / 3600000)).padStart(2, "0");
      const m = String(Math.floor((diff % 3600000) / 60000)).padStart(2, "0");
      const s = String(Math.floor((diff % 60000) / 1000)).padStart(2, "0");
      setTimeLeft(`${h}:${m}:${s}`);
    };

    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [banners]);
  if (!loading && !error && banners.length === 0) return null;
  const settings = {
    infinite: banners.length > 3,
    speed: 600,
    slidesToShow: banners.length >= 3 ? 3 : banners.length,
    slidesToScroll: 1,
    arrows: true,
    dots: false,
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    // responsive: [
    //   {
    //     breakpoint: 768,
    //     settings: {
    //       slidesToShow: 1,
    //     },
    //   },
    // ],
    responsive: [
      {
        breakpoint: 1024,
        settings: { slidesToShow: Math.min(3, banners.length) },
      },
      {
        breakpoint: 700,
        settings: { slidesToShow: Math.min(2, banners.length) },
      },
      {
        breakpoint: 480,
        settings: { slidesToShow: 1 },
      },
    ],
  };

  const goToDeal = (dealSlug) => {
    navigate(`/deal/${dealSlug}`);
  };

  const handleViewAll = () => {
    navigate("/flash-deals");
  };

  return (
    <section className="fd-section">
      {/* Stormy cloud + rain background */}
      <div className="fd-clouds">
        <div className="fd-cloud fd-cloud1"></div>
        <div className="fd-cloud fd-cloud2"></div>
        <div className="fd-cloud fd-cloud3"></div>
        <div className="fd-cloud fd-cloud4"></div>
        <div className="fd-cloud fd-cloud5"></div>

        <div className="fd-rain">
          {Array.from({ length: 40 }).map((_, i) => (
            <span
              key={i}
              className="fd-raindrop"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${0.6 + Math.random() * 0.6}s`,
              }}
            ></span>
          ))}
        </div>
      </div>

      <div className="fd-content">
        <h1 className="fd-title">FLASH DEALS</h1>
        <div className="fd-timer">
          <i className="las la-clock"></i>
          <span>
            {timeLeft === "ENDED"
              ? "Deals Ended"
              : timeLeft
                ? `Ends in ${timeLeft}`
                : ""}
          </span>
        </div>

        <div className="fd-carousel-wrap">
          {loading && <p className="fd-status-text">Loading deals...</p>}
          {error && <p className="fd-status-text">{error}</p>}

          {!loading && !error && banners.length > 0 && (
            <Slider {...settings}>
              {banners.map((item, index) => (
                <div className="fd-slide-item" key={item._id || index}>
                  <div className="fd-card" onClick={() => goToDeal(item.slug)}>
                    <div className="fd-card-top">
                      <DealImage
                        file={item.image}
                        alt={item.title || "Deal"}
                        className="fd-card-img"
                      />

                      {/* Logo — top left, image ke upar */}
                      {item.store_id?.logo && (
                        <DealImage
                          file={item.store_id.logo}
                          alt={item.store_id.name}
                          className="fd-logo-badge"
                          hideOnError
                        />
                      )}

                      {/* Discount % — top right, image ke upar */}
                      {item.offer_percent > 0 && (
                        <span className="fd-discount-badge">
                          -{item.offer_percent}%
                        </span>
                      )}

                      {/* Bottom overlay — price/cashback left, button right */}
                      <div className="fd-overlay-content">
                        <div className="fd-price-block">
                          {item.price ? (
                            <>
                              <span className="fd-price">₹{item.price}</span>
                              {item.mrp && Number(item.mrp) > Number(item.price) && (
                                <span className="fd-mrp">₹{item.mrp}</span>
                              )}
                            </>
                          ) : (
                            item.cashback_text && (
                              <span className="fd-cashback">
                                {item.cashback_text}
                              </span>
                            )
                          )}
                        </div>

                        <button
                          className="fd-grab-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            goToDeal(item.slug);
                          }}
                        >
                          Grab Deal →
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </Slider>
          )}
        </div>

        <button className="fd-view-all-btn" onClick={handleViewAll}>
          View All
        </button>
      </div>
    </section>
  );
}

export default FlashDeals;